import { useQueryClient } from '@tanstack/react-query'
import {
  AutoComplete,
  Checkbox,
  Collapse,
  Input,
  Popover,
  Select,
  Spin,
  Table,
  Tooltip,
  type TableProps
} from 'antd'
import clsx from 'clsx'
import { useAtomValue } from 'jotai'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type Key,
  type KeyboardEvent,
  type ReactNode
} from 'react'
import { FaPlus, FaQuestionCircle } from 'react-icons/fa'

import { Alert, Button, NormalizedDatePicker } from '@/components/elements'
import {
  ACTIONS,
  REQUIRED_MARK,
  SHIPMENT_ORDER_CSV_IMPORT
} from '@/constants/common/message.constants'
import { CustomerCreateModal } from '@/features/customers/components'
import { customersQueryKeys } from '@/features/customers/query-keys'
import { PackageCategoryCreateModal } from '@/features/package-categories/components'
import { packageCategoriesQueryKeys } from '@/features/package-categories/query-keys'
import { ShipmentCategoryCreateModal } from '@/features/shipment-categories/components'
import { shipmentCategoriesQueryKeys } from '@/features/shipment-categories/query-keys'
import { SiteModal } from '@/features/sites/components'
import { sitesQueryKeys } from '@/features/sites/query-keys'
import { TruckModelCreateModal } from '@/features/truck-models/components'
import { truckModelsQueryKeys } from '@/features/truck-models/query-keys'
import { selectedDcIdAtom } from '@/routes/_protected/$tenantKey/-states'

import { useViewportTableHeight } from '../../hooks'
import {
  DATE_FIELDS,
  LOGI_FIELDS,
  MASTER_REFERENCE_FIELDS,
  type EffectiveTable,
  type ImportMethod,
  type LogiField,
  type LogiFieldValue,
  type MasterCandidatesByField,
  type PreviewCellError,
  type PreviewDataRow,
  type PreviewWithServerCheck,
  toDate
} from '../../models'

/**
 * 表の高さを決めるときに、表以外（モーダル枠・ステップ・操作行・フッター）が使う縦幅の見込み。
 * 表は仮想スクロールで全行を扱うため、行数の上限は設けず、残りの高さを全部表に回す。
 */
const PREVIEW_TABLE_RESERVED_HEIGHT = 330

const LOGI_FIELD_LABEL_OF = Object.fromEntries(
  LOGI_FIELDS.map((f) => [f.value, f.label])
) as Record<LogiFieldValue, string>
/** 一括入力パネル（見出し＋入力欄1段）を開いているときに表から差し引く縦幅。 */
const PREVIEW_BULK_FILL_PANEL_HEIGHT = 100

type EditableCellProps = {
  rowNo: number
  field: LogiField
  value: string
  /** 表に出す文字（名称表示のときは「コード/名称」）。省略時は value */
  displayValue?: string
  reasons: string[]
  /** 吹き出しを開いたときだけ描画する編集欄。 */
  editor: () => ReactNode
}

/**
 * エラー/修正済みセル。普段は値を1行で表示し、ホバーでエラー内容、クリックで
 * 編集欄を吹き出しで開く。エラー文言は吹き出しの中でも編集欄の直下に置く。
 */
const EditableCell: FC<EditableCellProps> = ({
  rowNo,
  field,
  value,
  displayValue,
  reasons,
  editor
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)
  const hasError = reasons.length > 0

  // クリックで開いた直後はフォーカスが表のセルに残り、Esc が吹き出しを素通りして
  // モーダルを閉じてしまう。開いたら編集欄へフォーカスを移し、Esc を吹き出しで受ける。
  useEffect(() => {
    if (!isOpen) return
    const id = requestAnimationFrame(() => {
      const content = contentRef.current
      if (!content || content.contains(document.activeElement)) return
      const focusable = content.querySelector<HTMLElement>(
        'input:not([disabled]), textarea:not([disabled]), .ant-select-selector'
      )
      ;(focusable ?? content).focus()
    })
    return () => cancelAnimationFrame(id)
  }, [isOpen])

  const closeOnEscape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape' || !isOpen) return
    e.stopPropagation()
    setIsOpen(false)
  }

  return (
    <Popover
      trigger="click"
      open={isOpen}
      onOpenChange={setIsOpen}
      placement="bottomLeft"
      title={
        <span className="text-xs">
          {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_CELL_EDIT_TITLE(
            field.label,
            rowNo
          )}
        </span>
      }
      content={
        // Esc は吹き出しだけを閉じる。外側のモーダルまで伝わると取り込み作業ごと
        // 閉じてしまうため、ここで止める。
        // Enter で確定すると編集欄が blur してフォーカスが body に落ちるため、吹き出しへ戻す。
        <div
          ref={contentRef}
          tabIndex={-1}
          className="flex w-72 flex-col gap-1 outline-none"
          onKeyDown={(e) => {
            closeOnEscape(e)
            // 日本語入力の変換を確定する Enter では何もしない（フォーカスを移すと、検索中の候補の一覧が
            // 閉じて打った文字も消える）
            if (e.key === 'Enter' && !isImeComposing(e)) {
              setTimeout(() => contentRef.current?.focus())
            }
          }}
          data-testid={`shipment-order-csv-import__cell-editor-${rowNo}-${field.value}`}
        >
          {editor()}
          {reasons.map((reason, i) => (
            <span key={i} className="text-2xs text-red">
              {reason}
            </span>
          ))}
          <span className="text-3xs text-gray-2">
            {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_CELL_EDIT_HINT}
          </span>
        </div>
      }
    >
      <Tooltip
        open={isOpen ? false : undefined}
        title={
          hasError ? (
            <div className="flex flex-col gap-0.5">
              {reasons.map((reason, i) => (
                <span key={i} className="text-2xs">
                  {reason}
                </span>
              ))}
            </div>
          ) : (
            SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_CELL_EDITED
          )
        }
      >
        <span
          role="button"
          tabIndex={0}
          onKeyDown={closeOnEscape}
          // 表の他のセル（行の高さの中で上下中央）と文字の位置を揃える
          style={{ lineHeight: 'inherit' }}
          className={clsx(
            'inline-block max-w-full cursor-pointer truncate align-bottom text-xs',
            hasError && 'font-medium text-red',
            value === '' && 'text-gray-2'
          )}
          title={value === '' ? undefined : (displayValue ?? value)}
          data-testid={`shipment-order-csv-import__cell-${rowNo}-${field.value}`}
        >
          {value === ''
            ? SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_EMPTY_CELL
            : (displayValue ?? value)}
        </span>
      </Tooltip>
    </Popover>
  )
}

/**
 * 「登録」ボタンで開くモーダルの種類。既存の本番作成モーダル（設定 > 各マスタ画面と共通）を
 * そのまま再利用する対応関係——ここで新規に作成フォームを作らない。
 */
type MasterRegisterModalKind =
  | 'customer'
  | 'shipmentCategory'
  | 'site'
  | 'truckModel'
  | 'packageCategory'

/**
 * 登録ボタンを出すマスタ参照項目 → 再利用する本番作成モーダルの対応。
 * partnerId は対応する再利用可能な作成モーダルが存在しない（features/partners/components に
 * *Modal* 系コンポーネントが無い）ため対象外（候補から選ぶだけ）。
 */
const MASTER_REGISTER_MODAL_KIND: Partial<
  Record<LogiFieldValue, MasterRegisterModalKind>
> = {
  customerId: 'customer',
  shipmentCategoryId: 'shipmentCategory',
  originSiteId: 'site',
  destSiteId: 'site',
  truckModelId: 'truckModel',
  packageCategoryId: 'packageCategory'
}

/**
 * 登録成功後に invalidate するクエリ（kind別）。masterCandidates の取得元
 * （useMasterReferenceCandidates）と一致させる——各 feature の query-keys.ts の
 * `all` を使う。
 */
const REGISTER_INVALIDATE_QUERY_KEY: Record<
  MasterRegisterModalKind,
  readonly unknown[]
> = {
  customer: customersQueryKeys.all,
  shipmentCategory: shipmentCategoriesQueryKeys.all,
  site: sitesQueryKeys.all,
  truckModel: truckModelsQueryKeys.all,
  packageCategory: packageCategoriesQueryKeys.all
}

/** 名称表示のときのマスタ参照項目の列幅（「コード/名称」が読めるように広げる） */
const MASTER_NAME_COLUMN_WIDTH = 240
const DEFAULT_COLUMN_WIDTH = 140

/**
 * 日本語入力（IME）の変換を確定するための Enter か。変換中の keydown は isComposing が立ち、
 * 古いブラウザでは keyCode が 229 になる
 */
const isImeComposing = (e: KeyboardEvent): boolean =>
  e.nativeEvent.isComposing || e.keyCode === 229

/** マスタの候補をセレクトの選択肢（値はコード、表示は「コード/名称」）にする */
const toMasterOptions = (
  candidates: MasterCandidatesByField[LogiFieldValue]
): { value: string; label: string }[] =>
  (candidates ?? []).map((m) => ({
    value: m.code,
    label: `${m.code}/${m.name}`
  }))

const filterMasterOption = (
  input: string,
  option?: { label?: unknown }
): boolean =>
  typeof option?.label === 'string' &&
  option.label.toLowerCase().includes(input.toLowerCase())

/**
 * 手入力欄の値（CSVセルの文字列、"2026/09/01" 等）をカレンダー表示用の Date に変換する。
 * 空欄・読み取れない値・実在しない日付は null（カレンダーは未選択表示になる）。
 * 日付欄の手入力・一括入力は素のテキスト欄でなくカレンダー選択（NormalizedDatePicker）にするため。
 * 確認画面の判定と同じ読み方（toDate）にする。new Date は 2026/02/31 を 2026/03/03 に繰り上げるので、
 * それを初期値にすると直していない日付がカレンダーに入り、閉じただけで繰り上げた日付が確定してしまう。
 */
const parseDateValue = (raw: string): Date | null =>
  raw.trim() ? toDate(raw) : null

/** フォーマット保存欄の状態（マッピング内容を次回初期値として残す任意操作）。 */
export type SaveFormatDraft = {
  enabled: boolean
  name: string
}

/** 一括入力の欄に出せる項目（必須項目に限る、#5787） */
const BULK_FILL_FIELD_DEFS = LOGI_FIELDS.filter((f) => f.required)

/** 項目ごとの、エラーのある行の行番号 */
const errorRowNosByFieldOf = (
  rows: readonly {
    rowNo: number
    errors: readonly { field: LogiFieldValue }[]
  }[]
): Map<LogiFieldValue, number[]> => {
  // 1 行に同じ項目のエラーが重なることがあるので Set で重複を除く（行数が多くても線形で済むように）
  const sets = new Map<LogiFieldValue, Set<number>>()
  for (const row of rows) {
    for (const error of row.errors) {
      const rowNos = sets.get(error.field) ?? new Set<number>()
      rowNos.add(row.rowNo)
      sets.set(error.field, rowNos)
    }
  }
  return new Map([...sets].map(([field, rowNos]) => [field, [...rowNos]]))
}

/**
 * 項目ごとの、一括入力の対象の行（#5787）: その項目にエラーのある行と、確認画面で直した行。
 * 直した行もエラーのセルと同じく直し続けられるようにする（一度もエラーにならなかった行は対象にしない）
 */
const bulkFillTargetRowNosByFieldOf = (
  rows: readonly {
    rowNo: number
    errors: readonly { field: LogiFieldValue }[]
    editedFields: readonly LogiFieldValue[]
  }[]
): Map<LogiFieldValue, number[]> =>
  errorRowNosByFieldOf(
    rows.map((row) => ({
      rowNo: row.rowNo,
      errors: [...row.errors, ...row.editedFields.map((field) => ({ field }))]
    }))
  )

/** 一括入力の欄で対にする項目の 1 つ目（荷主・配送種別／配送日・着日／発地・着地）。段の頭に置く */
const BULK_FILL_PAIR_HEADS: ReadonlySet<LogiFieldValue> =
  new Set<LogiFieldValue>(['customerId', 'shippingDate', 'originSiteId'])

type Props = {
  method: ImportMethod
  /** SiteModal（地点の新規登録）呼び出しに必要。 */
  tenantKey: string
  /** 通常フォーマットで標準列が1つも一致しない場合の生表示に使う。 */
  effectiveTable: EffectiveTable
  /**
   * 形式チェック済みプレビュー。汎用＝ユーザーのマッピング、通常＝標準導出マッピング。
   * null は「通常フォーマットで標準列が1列も一致しない」場合のみ（不一致警告を出す）。
   */
  preview: PreviewWithServerCheck | null
  /** マスタ参照項目（荷主等）の実マスタ検索候補（一括入力パネルの選択肢）。 */
  masterCandidates: MasterCandidatesByField
  /** エラーセルのインライン修正（rowNo・項目・修正後の値）。修正後に再検証される。 */
  onEditCell: (rowNo: number, field: LogiFieldValue, value: string) => void
  /**
   * 「列が無いので後で手入力する」とマークされた必須項目。
   * 一括入力パネルの対象になる（マッピング画面でマークした項目のみ）。
   */
  skippedFields: ReadonlySet<LogiFieldValue>
  /**
   * 指定項目の値を一括で流し込む（＝「埋める」だけで自動登録はしない）。
   * 表で行を選んでいれば rowNos にその行を渡してその行だけに、選んでいなければ rowNos を渡さず全行に入れる。
   * 内部は対象の行をまとめて1回の状態更新で適用する。適用後は通常どおり
   * 内容を確認した上で取り込み実行ボタンを押す必要がある。
   */
  onBulkFill: (
    field: LogiFieldValue,
    value: string,
    rowNos?: readonly number[]
  ) => void
  excludeErrors: boolean
  onChangeExcludeErrors: (next: boolean) => void
  saveFormat: SaveFormatDraft
  onChangeSaveFormat: (next: SaveFormatDraft) => void
  /**
   * 事前チェック（取り込まずにサーバー側の検証だけを走らせる）の状態。
   * マスタ突合・明細ID の重複など、画面側では判定できない問題を確認画面で見せるために使う。
   */
  serverCheckStatus: ServerCheckStatus
  /** 事前チェックをやり直す（修正後に最新の値で確かめ直すとき）。 */
  onRecheck: () => void
}

export type ServerCheckStatus = 'idle' | 'checking' | 'done' | 'failed'

type GenericRow = PreviewDataRow & { key: number }
type StandardRow = { key: number; rowNo: number; cells: string[] }

/**
 * 確認画面（プレビュー・エラー確認）。全経路（通常/保存フォーマット/フォーマット外）が
 * 取り込み実行前にここを通る。
 *
 * - 通常/汎用とも同じ行単位エラー表示に統一（通常は標準導出マッピングで buildPreview を共用）。
 * - エラーセルはその場で修正できる（修正すると再検証。取り込みデータにのみ反映、元ファイル不変）。
 * - 通常フォーマットで標準列が1列も一致しない場合のみ、不一致警告＋生テーブルを表示する。
 */
export const PreviewView: FC<Props> = ({
  method,
  tenantKey,
  effectiveTable,
  preview,
  masterCandidates,
  onEditCell,
  skippedFields,
  onBulkFill,
  excludeErrors,
  onChangeExcludeErrors,
  saveFormat,
  onChangeSaveFormat,
  serverCheckStatus,
  onRecheck
}) => {
  const isStandard = method === 'standard'
  const isStandardMismatch = isStandard && preview === null
  // 一括入力パネルの入力欄（項目value → 未適用の下書き値）。適用ボタンを押すまで反映しない。
  const [bulkFillDrafts, setBulkFillDrafts] = useState<
    Partial<Record<LogiFieldValue, string>>
  >({})
  // 一括入力の欄に出す項目（#5787）: 必須項目のうち、列が無い項目と、エラーのある行がある項目。
  // 一度出した項目は、直してエラーが 0 件になっても欄から消さない（直している途中で欄が動かないように）
  const errorRowNosByField = useMemo(
    () => errorRowNosByFieldOf(preview?.rows ?? []),
    [preview]
  )
  const bulkFillTargetRowNosByField = useMemo(
    () => bulkFillTargetRowNosByFieldOf(preview?.rows ?? []),
    [preview]
  )
  const bulkFillTargetsNow = useCallback(
    (errorRows: Map<LogiFieldValue, number[]>) =>
      BULK_FILL_FIELD_DEFS.filter(
        (f) =>
          skippedFields.has(f.value) ||
          (errorRows.get(f.value)?.length ?? 0) > 0
      ).map((f) => f.value),
    [skippedFields]
  )
  const [bulkFillShownFields, setBulkFillShownFields] = useState<
    ReadonlySet<LogiFieldValue>
  >(() => new Set(bulkFillTargetsNow(errorRowNosByField)))
  useEffect(() => {
    setBulkFillShownFields((prev) => {
      const added = bulkFillTargetsNow(errorRowNosByField).filter(
        (field) => !prev.has(field)
      )
      return added.length === 0 ? prev : new Set([...prev, ...added])
    })
  }, [bulkFillTargetsNow, errorRowNosByField])
  const bulkFillFieldDefs = useMemo(
    () => BULK_FILL_FIELD_DEFS.filter((f) => bulkFillShownFields.has(f.value)),
    [bulkFillShownFields]
  )
  // 表の行のチェックを押せる行（欄に出ている項目のどれかで一括入力の対象になる行）。行ごとに配列を探すと
  // 行数の 2 乗になるので、先に 1 つの Set にまとめておく
  const bulkFillSelectableRowNos = useMemo(
    () =>
      new Set(
        bulkFillFieldDefs.flatMap(
          (f) => bulkFillTargetRowNosByField.get(f.value) ?? []
        )
      ),
    [bulkFillFieldDefs, bulkFillTargetRowNosByField]
  )
  const dcId = useAtomValue(selectedDcIdAtom)
  const queryClient = useQueryClient()
  // マスタ参照項目（荷主/配送種別/発着地/車種/荷姿）の「登録」ボタンで開く作成モーダルの対象
  const [registerTarget, setRegisterTarget] = useState<{
    rowNo: number
    field: LogiFieldValue
    kind: MasterRegisterModalKind
    initialValue: string
  } | null>(null)

  const handleMasterRegisterSuccess = useCallback(
    async (code: string) => {
      if (!registerTarget) return
      const { rowNo, field, kind } = registerTarget
      onEditCell(rowNo, field, code)
      setRegisterTarget(null)
      // 新規登録したマスタがすぐ検索・一括入力パネルの候補に反映されるよう、
      // masterCandidates の取得元クエリ（kind別）を無効化する。
      await queryClient.invalidateQueries({
        queryKey: REGISTER_INVALIDATE_QUERY_KEY[kind]
      })
    },
    [registerTarget, onEditCell, queryClient]
  )
  // マスタ参照項目（荷主/発着地等）の名称表示トグル。
  // 存在チェックのために取得済みの実マスタ候補（masterCandidates）に名称も
  // 含まれているため、追加のAPI呼び出しなしでコード→名称の対応表を作れる。
  const [showMasterNames, setShowMasterNames] = useState(false)
  const masterCodeToName = useMemo(() => {
    const map: Partial<Record<LogiFieldValue, Map<string, string>>> = {}
    for (const field of MASTER_REFERENCE_FIELDS) {
      const candidates = masterCandidates[field]
      if (!candidates) continue
      map[field] = new Map(candidates.map((c) => [c.code, c.name]))
    }
    return map
  }, [masterCandidates])

  /**
   * エラー/修正済みセルの編集欄（吹き出しの中身）。セル自体は1行表示のまま保ち、
   * 入力欄・検索/登録ボタンはクリックで開く吹き出しの中にだけ出す——常時描画すると
   * 行の高さが倍になり、表に数行しか入らなくなるため。
   */
  const renderCellEditor = useCallback(
    (
      row: GenericRow,
      field: LogiField,
      error: PreviewCellError | undefined,
      value: string
    ) => {
      if (DATE_FIELDS.has(field.value)) {
        return (
          <NormalizedDatePicker
            size="small"
            style={{ width: '100%' }}
            format="YYYY/MM/DD"
            value={parseDateValue(value)}
            status={error ? 'error' : undefined}
            onChange={(_, dateString) => {
              if (dateString && dateString !== value) {
                onEditCell(row.rowNo, field.value, dateString)
              }
            }}
            data-testid={`shipment-order-csv-import__edit-${row.rowNo}-${field.value}`}
          />
        )
      }
      // マスタ参照項目（荷主/配送種別/発着地/パートナー/車種/荷姿）は、一括入力欄と同じ候補
      // （確認画面で取得済みの実マスタ）から選ぶ。送る値はコード（取込のマスタ突合がコード完全一致のため）。
      // 候補に無いものは「登録」ボタンで既存の作成モーダルを開いて作る（作成モーダルが無い partnerId は除く）
      if (MASTER_REFERENCE_FIELDS.has(field.value)) {
        const registerModalKind = MASTER_REGISTER_MODAL_KIND[field.value]
        return (
          <div className="flex items-center gap-1">
            <Select
              size="small"
              className="min-w-0 flex-1"
              showSearch
              defaultOpen
              status={error ? 'error' : undefined}
              value={value || undefined}
              options={toMasterOptions(masterCandidates[field.value])}
              filterOption={filterMasterOption}
              onChange={(next: string) => {
                if (next !== value) onEditCell(row.rowNo, field.value, next)
              }}
              data-testid={`shipment-order-csv-import__edit-${row.rowNo}-${field.value}`}
            />
            {registerModalKind && (
              <Tooltip title={ACTIONS.REGISTER}>
                <Button
                  size="small"
                  icon={<FaPlus size={10} />}
                  onClick={() =>
                    setRegisterTarget({
                      rowNo: row.rowNo,
                      field: field.value,
                      kind: registerModalKind,
                      initialValue: value.trim()
                    })
                  }
                  data-testid={`shipment-order-csv-import__cell-register-${row.rowNo}-${field.value}`}
                />
              </Tooltip>
            )}
          </div>
        )
      }
      return (
        <Input
          key={`${row.rowNo}-${field.value}-${value}`}
          size="small"
          defaultValue={value}
          status={error ? 'error' : undefined}
          onBlur={(e) => {
            if (e.target.value !== value) {
              onEditCell(row.rowNo, field.value, e.target.value)
            }
          }}
          onPressEnter={(e) => {
            // 日本語入力の変換を確定する Enter では、値を確定せず入力を続けさせる
            if (isImeComposing(e)) return
            ;(e.target as HTMLInputElement).blur()
          }}
          data-testid={`shipment-order-csv-import__edit-${row.rowNo}-${field.value}`}
        />
      )
    },
    [onEditCell, masterCandidates]
  )

  // 行/列の絞り込み。エラーを直す作業では「直す必要のある行と列」だけ見えている状態を
  // 基本にできるようにする（横スクロールしないと見えない列のエラーを取りこぼさないため）。
  // 出すのは「絞り込みをオンにした時点でエラーがあった行・列」と「今エラーがある行・列」。
  // 直した行・列を丸ごと出すと、一括入力で全行に値を入れたときに全行・その列が残って絞り込めないため、
  // オンにした時点のエラーを覚えておき、直してエラーが消えても絞り込み中は出したままにする
  // 一括入力を入れる行（#5787）。選んでいなければ一括入力はエラーのある行すべてに入る。表に出る行が変わったら（列の
  // 絞り込み・エラー行だけ表示）選択を外す——隠れた行が選ばれたまま残ると、見えていない行にも入ってしまうため
  const [selectedRowNos, setSelectedRowNos] = useState<number[]>([])
  // 列の見出しで絞り込んでいるか。絞り込みだけでは一括入力の対象は狭まらないので、行を選んでいなければ案内を出す
  const [isColumnFiltered, setIsColumnFiltered] = useState(false)
  // 一括入力を適用したときに値が入る行の数（その項目にエラーのある行と直した行。行を選択していれば、そのうちの行）
  const bulkFillTargetCountOf = (field: LogiFieldValue): number => {
    const targetRows = bulkFillTargetRowNosByField.get(field) ?? []
    if (selectedRowNos.length === 0) return targetRows.length
    const selected = new Set(selectedRowNos)
    return targetRows.filter((rowNo) => selected.has(rowNo)).length
  }
  // 列の見出しの絞り込みの候補（その列に出てくる値）
  const filterOptionsByField = useMemo(() => {
    const map = new Map<LogiFieldValue, { text: string; value: string }[]>()
    for (const field of preview?.fields ?? []) {
      const values = new Set<string>()
      for (const row of preview?.rows ?? []) {
        values.add(row.cells[field.value] ?? '')
      }
      map.set(
        field.value,
        [...values].sort().map((value) => ({
          text:
            value === '' ? SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_EMPTY_CELL : value,
          value
        }))
      )
    }
    return map
  }, [preview])
  const [errorRowsAtFilter, setErrorRowsAtFilter] =
    useState<ReadonlySet<number> | null>(null)
  const [errorFieldsAtFilter, setErrorFieldsAtFilter] =
    useState<ReadonlySet<LogiFieldValue> | null>(null)
  const isErrorRowsOnly = errorRowsAtFilter !== null
  const isErrorColumnsOnly = errorFieldsAtFilter !== null
  const onChangeErrorRowsOnly = (checked: boolean) => {
    setSelectedRowNos([])
    setErrorRowsAtFilter(
      checked
        ? new Set(
            (preview?.rows ?? [])
              .filter(
                (row) => row.errors.length > 0 || row.rowErrors.length > 0
              )
              .map((row) => row.rowNo)
          )
        : null
    )
  }
  const onChangeErrorColumnsOnly = (checked: boolean) =>
    setErrorFieldsAtFilter(
      checked
        ? new Set(
            (preview?.rows ?? []).flatMap((row) =>
              row.errors.map((e) => e.field)
            )
          )
        : null
    )
  const attentionFields = useMemo(() => {
    const set = new Set<LogiFieldValue>(errorFieldsAtFilter ?? [])
    for (const row of preview?.rows ?? []) {
      for (const e of row.errors) set.add(e.field)
    }
    return set
  }, [preview, errorFieldsAtFilter])

  const genericColumns: TableProps<GenericRow>['columns'] = useMemo(() => {
    if (!preview) return []
    const fieldLabel = new Map(preview.fields.map((f) => [f.value, f.label]))
    const rowNoColumn = {
      title: SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ROW_NO_HEADER,
      key: 'rowNo',
      width: 56,
      fixed: 'left' as const,
      align: 'center' as const,
      render: (_: unknown, row: GenericRow) =>
        row.errors.length > 0 || row.rowErrors.length > 0 ? (
          <Tooltip
            title={
              <div className="flex flex-col gap-0.5">
                <span className="font-medium">
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ROW_ERRORS_TITLE(
                    row.rowNo
                  )}
                </span>
                {row.rowErrors.map((message, i) => (
                  <span key={`row-${i}`} className="text-2xs">
                    {message}
                  </span>
                ))}
                {row.errors.map((e, i) => (
                  <span key={i} className="text-2xs">
                    {fieldLabel.get(e.field) ?? e.field}: {e.reason}
                  </span>
                ))}
              </div>
            }
          >
            <span
              className="cursor-default font-medium tabular-nums text-red"
              data-testid={`shipment-order-csv-import__row-errors-${row.rowNo}`}
            >
              {row.rowNo}
            </span>
          </Tooltip>
        ) : (
          <span className="tabular-nums text-secondary">{row.rowNo}</span>
        )
    }
    const visibleFields = isErrorColumnsOnly
      ? preview.fields.filter((f) => attentionFields.has(f.value))
      : preview.fields
    const fieldColumns = visibleFields.map((field) => ({
      title: (
        <span className="text-3xs text-secondary">
          {field.label}
          {field.required && (
            <span className="ml-0.5 text-red">{REQUIRED_MARK}</span>
          )}
        </span>
      ),
      key: field.value,
      filters: filterOptionsByField.get(field.value),
      filterSearch: true,
      onFilter: (value: Key | boolean, row: GenericRow) =>
        (row.cells[field.value] ?? '') === value,
      width:
        showMasterNames && MASTER_REFERENCE_FIELDS.has(field.value)
          ? MASTER_NAME_COLUMN_WIDTH
          : DEFAULT_COLUMN_WIDTH,
      ellipsis: true,
      onCell: (row: GenericRow) => {
        const hasError = row.errors.some((e) => e.field === field.value)
        const isEdited = row.editedFields.includes(field.value)
        return {
          className: clsx(
            // antd red-2 / blue-1 相当。palette に段階色が無いため arbitrary value
            hasError && '!bg-[#ffccc7]',
            !hasError && isEdited && '!bg-[#e6f4ff]'
          )
        }
      },
      render: (_: unknown, row: GenericRow) => {
        const errors = row.errors.filter((e) => e.field === field.value)
        const isEdited = row.editedFields.includes(field.value)
        const value = row.cells[field.value] ?? ''
        const name =
          showMasterNames && value !== ''
            ? masterCodeToName[field.value]?.get(value)
            : undefined
        if (errors.length > 0 || isEdited) {
          return (
            <EditableCell
              rowNo={row.rowNo}
              field={field}
              value={value}
              displayValue={name ? `${value}/${name}` : undefined}
              reasons={errors.map((e) => e.reason)}
              editor={() => renderCellEditor(row, field, errors[0], value)}
            />
          )
        }
        if (name) {
          return (
            <span className="text-xs" title={`${value}/${name}`}>
              {value}/{name}
            </span>
          )
        }
        return <span className="text-xs">{value}</span>
      }
    }))
    return [rowNoColumn, ...fieldColumns]
  }, [
    preview,
    isErrorColumnsOnly,
    attentionFields,
    filterOptionsByField,
    showMasterNames,
    masterCodeToName,
    renderCellEditor
  ])

  // 仮想スクロールの表は scroll.x に数値が要る（列幅の合計）。
  const genericScrollX = useMemo(
    () =>
      (genericColumns ?? []).reduce(
        (sum, col) => sum + (typeof col.width === 'number' ? col.width : 0),
        0
      ),
    [genericColumns]
  )

  const genericRows: GenericRow[] = useMemo(
    () =>
      (preview?.rows ?? [])
        .filter(
          (row) =>
            errorRowsAtFilter === null ||
            errorRowsAtFilter.has(row.rowNo) ||
            row.errors.length > 0 ||
            row.rowErrors.length > 0
        )
        .map((row) => ({ ...row, key: row.rowNo })),
    [preview, errorRowsAtFilter]
  )

  // 標準列が1列も一致しない場合の生テーブル（不一致警告と併置）
  const standardColumns: TableProps<StandardRow>['columns'] = useMemo(() => {
    const rowNoColumn = {
      title: SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ROW_NO_HEADER,
      key: 'rowNo',
      width: 48,
      fixed: 'left' as const,
      align: 'center' as const,
      render: (_: unknown, row: StandardRow) => (
        <span className="tabular-nums text-secondary">{row.rowNo}</span>
      )
    }
    const cellColumns = Array.from(
      { length: effectiveTable.columnCount },
      (_, idx) => ({
        title: (
          <span className="text-3xs text-secondary">
            {effectiveTable.headers[idx] ||
              SHIPMENT_ORDER_CSV_IMPORT.COLUMN_LABEL(idx)}
          </span>
        ),
        key: `col-${idx}`,
        width: 120,
        ellipsis: true,
        render: (_: unknown, row: StandardRow) => (
          <span className="text-xs">{row.cells[idx] ?? ''}</span>
        )
      })
    )
    return [rowNoColumn, ...cellColumns]
  }, [effectiveTable])

  const standardRows: StandardRow[] = useMemo(
    () =>
      effectiveTable.dataRows.map((cells, i) => ({
        key: i,
        rowNo: i + 1,
        cells
      })),
    [effectiveTable]
  )

  const totalCount = preview?.rows.length ?? effectiveTable.dataRows.length
  const errorCount = preview?.errorCount ?? 0
  const okCount = preview?.okCount ?? 0
  const tableHeight = useViewportTableHeight(
    PREVIEW_TABLE_RESERVED_HEIGHT +
      (!isStandardMismatch && bulkFillFieldDefs.length > 0
        ? PREVIEW_BULK_FILL_PANEL_HEIGHT
        : 0)
  )

  const helpText = (
    <span className="text-xs">
      {isStandard
        ? SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_STANDARD_NOTE
        : SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ALERT_DESCRIPTION}
      <br />
      {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_EDIT_HINT}
    </span>
  )

  const bundleKeyIgnoredCount = (preview?.rows ?? []).filter(
    (row) => !!row.cells.bundleKey?.trim() && !!row.cells.dispatchKey?.trim()
  ).length

  return (
    <div className="flex flex-col gap-2">
      {isStandardMismatch && (
        <Alert
          color="red"
          title={SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_ALERT_MESSAGE}
        >
          <span className="text-xs">
            {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_STANDARD_MISMATCH}
          </span>
        </Alert>
      )}

      {/* 一括入力パネル（必須項目のうち、マッピング画面で「後で手入力する」とマークした項目と、エラーのある行がある項目）。
          値を入れて適用すると、その項目にエラーのある行（行を選択していれば、そのうちエラーのある行）に入る。埋めるだけで自動登録はしない
          ——内容を確認した上で取り込み実行ボタンを別途押す。
          表に縦幅を回すため折りたたみにする（見出しに対象項目数を出す）。 */}
      {!isStandardMismatch && bulkFillFieldDefs.length > 0 && (
        <Collapse
          size="small"
          defaultActiveKey={['bulk-fill']}
          className="border-orange-5 bg-[#fff7e6]"
          items={[
            {
              key: 'bulk-fill',
              label: (
                <span className="text-xs font-medium text-secondary">
                  {SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_TITLE}
                  {SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_COUNT(
                    bulkFillFieldDefs.length
                  )}
                  <Tooltip
                    title={SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_DESCRIPTION}
                  >
                    <FaQuestionCircle
                      className="ml-1 inline text-gray-2"
                      size={11}
                    />
                  </Tooltip>
                </span>
              ),
              children: (
                // 入力欄の幅が項目ごとに違うので、折り返し（flex-wrap）だと発地と着地のような対の項目が
                // 段に分かれる。幅のそろった 2 列に並べ、対の 1 つ目（荷主／配送日／発地）を必ず段の頭に置いて、
                // 手入力にした項目の組み合わせが何であっても対が同じ段に並ぶようにする
                <div className="flex flex-col gap-2">
                  {isColumnFiltered && selectedRowNos.length === 0 && (
                    <div
                      className="text-xs text-secondary"
                      data-testid="shipment-order-csv-import__bulk-fill-filtered-guide"
                    >
                      {
                        SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_FILTERED_NO_SELECTION
                      }
                    </div>
                  )}
                  {selectedRowNos.length > 0 && (
                    <div
                      className="flex items-center gap-2 text-xs text-secondary"
                      data-testid="shipment-order-csv-import__bulk-fill-selection"
                    >
                      {SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_SELECTED_ROWS(
                        selectedRowNos.length
                      )}
                      <Button
                        type="link"
                        size="small"
                        className="!px-0 !text-xs"
                        onClick={() => setSelectedRowNos([])}
                        data-testid="shipment-order-csv-import__bulk-fill-clear-selection"
                      >
                        {SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_CLEAR_SELECTION}
                      </Button>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                    {bulkFillFieldDefs.map((field) => (
                      <div
                        key={field.value}
                        className={clsx(
                          'flex items-center gap-2',
                          BULK_FILL_PAIR_HEADS.has(field.value) && 'col-start-1'
                        )}
                        data-testid={`shipment-order-csv-import__bulk-fill-item-${field.value}`}
                      >
                        <span className="w-28 shrink-0 text-xs font-medium text-secondary">
                          {field.label}
                        </span>
                        {/* マスタ参照項目（customerId等）は実マスタ検索API（useMasterReferenceCandidates、
                  CustomerSelect等と同じ読み取り専用API）の
                  候補を「コード/名称」表示形式（features/customers/components/CustomerSelect/
                  map-to-customer-field-name.ts と同形式）で選ばせるSelect。送信される値は
                  コード（BEのマスタ突合がコード完全一致で行われるため）。
                  マスタ参照でない項目（日付等）は素の入力欄として振る舞う AutoComplete のまま。 */}
                        {masterCandidates[field.value] ? (
                          <Select
                            size="small"
                            // コード+名称の結合ラベル（例「demo00001/demo）田中一郎（静岡DC）」）が
                            // w-56（224px）だと選択済み値が読めない位置で省略されるため広めにとる。
                            // antd Select は選択値に text-overflow:ellipsis と title 属性
                            // （ホバーで全文表示）を標準で付与済みのため、幅を広げるだけで読みやすくなる。
                            className="w-72"
                            showSearch
                            allowClear
                            options={toMasterOptions(
                              masterCandidates[field.value]
                            )}
                            filterOption={filterMasterOption}
                            placeholder={
                              SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_PLACEHOLDER
                            }
                            value={bulkFillDrafts[field.value] || undefined}
                            onChange={(value) =>
                              setBulkFillDrafts((prev) => ({
                                ...prev,
                                [field.value]: value ?? ''
                              }))
                            }
                            data-testid={`shipment-order-csv-import__bulk-fill-input-${field.value}`}
                          />
                        ) : DATE_FIELDS.has(field.value) ? (
                          // 日付項目（発送日・着日等）はカレンダー選択（マスタ参照と同じく
                          // データ型が固定されている項目は素のテキスト欄にしない）。
                          <NormalizedDatePicker
                            size="small"
                            className="w-56"
                            format="YYYY/MM/DD"
                            value={parseDateValue(
                              bulkFillDrafts[field.value] ?? ''
                            )}
                            onChange={(_, dateString) =>
                              setBulkFillDrafts((prev) => ({
                                ...prev,
                                [field.value]: dateString
                              }))
                            }
                            data-testid={`shipment-order-csv-import__bulk-fill-input-${field.value}`}
                          />
                        ) : (
                          <AutoComplete
                            size="small"
                            className="w-56"
                            options={[]}
                            placeholder={
                              SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_PLACEHOLDER
                            }
                            value={bulkFillDrafts[field.value] ?? ''}
                            onChange={(value) =>
                              setBulkFillDrafts((prev) => ({
                                ...prev,
                                [field.value]: value
                              }))
                            }
                            data-testid={`shipment-order-csv-import__bulk-fill-input-${field.value}`}
                          />
                        )}
                        <Button
                          size="small"
                          disabled={
                            !bulkFillDrafts[field.value] ||
                            bulkFillTargetCountOf(field.value) === 0
                          }
                          onClick={() =>
                            selectedRowNos.length > 0
                              ? onBulkFill(
                                  field.value,
                                  bulkFillDrafts[field.value] ?? '',
                                  selectedRowNos
                                )
                              : onBulkFill(
                                  field.value,
                                  bulkFillDrafts[field.value] ?? ''
                                )
                          }
                          data-testid={`shipment-order-csv-import__bulk-fill-apply-${field.value}`}
                        >
                          {selectedRowNos.length > 0
                            ? SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_APPLY_SELECTED(
                                bulkFillTargetCountOf(field.value)
                              )
                            : SHIPMENT_ORDER_CSV_IMPORT.BULK_FILL_APPLY_ERROR_ROWS(
                                bulkFillTargetCountOf(field.value)
                              )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )
            }
          ]}
        />
      )}

      {/* 運行計画のある行のまとめキーは使わない（行エラーにはしない）。何行が無視されるかを出す */}
      {!isStandardMismatch && bundleKeyIgnoredCount > 0 && (
        <Alert
          color="yellow"
          title={SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_BUNDLE_KEY_IGNORED_TITLE(
            bundleKeyIgnoredCount
          )}
          data-testid="shipment-order-csv-import__bundle-key-ignored"
        >
          <span className="text-xs">
            {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_BUNDLE_KEY_IGNORED_DESCRIPTION}
          </span>
        </Alert>
      )}

      {/* 行に紐づかない問題（ファイル全体の問題）。事前チェックで見つかったもの */}
      {!isStandardMismatch && (preview?.globalIssues.length ?? 0) > 0 && (
        <Alert
          color="red"
          title={SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_GLOBAL_ISSUES_TITLE}
        >
          <ul
            className="m-0 list-disc pl-4 text-xs"
            data-testid="shipment-order-csv-import__global-issues"
          >
            {preview?.globalIssues.map((message, i) => (
              <li key={i}>{message}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* 表の上の操作行: 件数・絞り込み・名称表示・説明（?）を1行にまとめる */}
      {!isStandardMismatch && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <div
            className="flex items-center gap-3 text-xs font-medium"
            data-testid="shipment-order-csv-import__preview-summary"
          >
            <span className="text-secondary">
              {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SUMMARY_TOTAL(totalCount)}
            </span>
            <span className="text-green">
              {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SUMMARY_OK(okCount)}
            </span>
            <span
              className={clsx(errorCount > 0 ? 'text-red' : 'text-secondary')}
            >
              {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SUMMARY_ERROR(errorCount)}
            </span>
            <Tooltip title={helpText} overlayStyle={{ maxWidth: 480 }}>
              <FaQuestionCircle
                className="cursor-help text-gray-2"
                size={12}
                data-testid="shipment-order-csv-import__preview-help"
              />
            </Tooltip>
            {(preview?.siteAutoCreates.length ?? 0) > 0 && (
              <Tooltip
                overlayStyle={{ maxWidth: 480 }}
                title={
                  <div className="flex flex-col gap-0.5">
                    {preview?.siteAutoCreates.map((s) => (
                      <span key={`${s.rowNo}-${s.field}`} className="text-2xs">
                        {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SITE_AUTO_CREATE_ITEM(
                          s.rowNo,
                          LOGI_FIELD_LABEL_OF[s.field],
                          s.code
                        )}
                      </span>
                    ))}
                  </div>
                }
              >
                <span
                  className="cursor-help font-normal text-secondary underline decoration-dotted"
                  data-testid="shipment-order-csv-import__site-auto-creates"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SITE_AUTO_CREATE_COUNT(
                    preview?.siteAutoCreates.length ?? 0
                  )}
                </span>
              </Tooltip>
            )}
            <span
              className="flex items-center gap-1 font-normal text-secondary"
              data-testid="shipment-order-csv-import__server-check-status"
            >
              {serverCheckStatus === 'checking' && (
                <>
                  <Spin size="small" />
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SERVER_CHECKING}
                </>
              )}
              {serverCheckStatus === 'failed' && (
                <span className="text-red">
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SERVER_CHECK_FAILED}
                </span>
              )}
              {serverCheckStatus !== 'checking' && (
                <Button
                  size="small"
                  type="link"
                  className="!px-1 !text-xs"
                  onClick={onRecheck}
                  data-testid="shipment-order-csv-import__server-recheck"
                >
                  {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SERVER_RECHECK}
                </Button>
              )}
            </span>
          </div>
          <div className="ml-auto flex flex-wrap items-center gap-x-3">
            <Checkbox
              checked={isErrorRowsOnly}
              onChange={(e) => onChangeErrorRowsOnly(e.target.checked)}
              data-testid="shipment-order-csv-import__filter-error-rows"
            >
              <span className="text-xs">
                {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_FILTER_ERROR_ROWS}
              </span>
            </Checkbox>
            <Checkbox
              checked={isErrorColumnsOnly}
              onChange={(e) => onChangeErrorColumnsOnly(e.target.checked)}
              data-testid="shipment-order-csv-import__filter-error-columns"
            >
              <span className="text-xs">
                {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_FILTER_ERROR_COLUMNS}
              </span>
            </Checkbox>
            {/* マスタ参照項目（荷主/発着地等）の名称表示トグル。 */}
            <Checkbox
              checked={showMasterNames}
              onChange={(e) => setShowMasterNames(e.target.checked)}
              data-testid="shipment-order-csv-import__toggle-master-names"
            >
              <span className="text-xs">
                {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_SHOW_MASTER_NAMES}
              </span>
            </Checkbox>
          </div>
        </div>
      )}

      {isStandardMismatch ? (
        <Table<StandardRow>
          size="small"
          bordered
          virtual
          rowKey="key"
          pagination={false}
          columns={standardColumns}
          dataSource={standardRows}
          scroll={{
            x: 48 + effectiveTable.columnCount * 120,
            y: tableHeight
          }}
        />
      ) : (
        <Table<GenericRow>
          size="small"
          bordered
          virtual
          rowKey="key"
          pagination={false}
          columns={genericColumns}
          dataSource={genericRows}
          // 行の選択は一括入力の対象を絞るためだけのものなので、一括入力の欄が出ているときだけ出す
          rowSelection={
            bulkFillFieldDefs.length > 0
              ? {
                  selectedRowKeys: selectedRowNos,
                  onChange: (keys) => setSelectedRowNos(keys.map(Number)),
                  // 欄に出ている項目のどれでも一括入力の対象にならない行（エラーが無く、直してもいない行）は、
                  // 選択しても値が入らないので押せなくする
                  getCheckboxProps: (row: GenericRow) => ({
                    disabled: !bulkFillSelectableRowNos.has(row.rowNo)
                  }),
                  columnWidth: 32
                }
              : undefined
          }
          onChange={(_, filters) => {
            setSelectedRowNos([])
            setIsColumnFiltered(
              Object.values(filters).some(
                (values) => values !== null && values.length > 0
              )
            )
          }}
          scroll={{ x: genericScrollX, y: tableHeight }}
        />
      )}

      {/* エラー行の扱い（通常/汎用共通）とフォーマット保存（汎用のみ）を表の下の1行にまとめる。
          保存＝マッピング内容を次回初期値として残す任意操作。 */}
      {!isStandardMismatch && (errorCount > 0 || method === 'adhoc') && (
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          {errorCount > 0 && (
            <Checkbox
              checked={excludeErrors}
              onChange={(e) => onChangeExcludeErrors(e.target.checked)}
              data-testid="shipment-order-csv-import__exclude-errors"
            >
              <span className="text-xs">
                {SHIPMENT_ORDER_CSV_IMPORT.PREVIEW_EXCLUDE_ERRORS}
              </span>
            </Checkbox>
          )}
          {/* 保存フォーマットで取り込むときの保存は、マッピング画面で選んだとおりにする（ここでは出さない） */}
          {method === 'adhoc' && (
            <span className="flex items-center gap-2">
              <Checkbox
                checked={saveFormat.enabled}
                onChange={(e) =>
                  onChangeSaveFormat({
                    ...saveFormat,
                    enabled: e.target.checked
                  })
                }
                data-testid="shipment-order-csv-import__save-format"
              >
                <span className="text-xs">
                  {SHIPMENT_ORDER_CSV_IMPORT.SAVE_FORMAT_TITLE}
                </span>
              </Checkbox>
              <Tooltip
                title={SHIPMENT_ORDER_CSV_IMPORT.SAVE_FORMAT_DESCRIPTION}
              >
                <FaQuestionCircle className="text-gray-2" size={11} />
              </Tooltip>
              {saveFormat.enabled && (
                <Input
                  size="small"
                  className="w-72"
                  placeholder={
                    SHIPMENT_ORDER_CSV_IMPORT.SAVE_FORMAT_NAME_PLACEHOLDER
                  }
                  value={saveFormat.name}
                  onChange={(e) =>
                    onChangeSaveFormat({ ...saveFormat, name: e.target.value })
                  }
                  data-testid="shipment-order-csv-import__save-format-name"
                />
              )}
            </span>
          )}
        </div>
      )}

      {/* マスタ参照項目の新規登録モーダル。既存の本番機能
          （設定 > 各マスタ画面の新規作成フロー）をそのまま再利用する——新規に作成
          フォームを作らない。成功時は新規レコードのコードを該当セルへ反映
          （既存のonEditCellと同じ経路、再検証も既存どおり走る）。
          地点（SiteModal）のみ、セル入力値をname/codeの初期値として事前入力する
          （SiteModal が初期値の受け口を持つため）。他4種は事前入力せず空欄で開く。 */}
      {registerTarget?.kind === 'site' && (
        <SiteModal
          open
          tenantKey={tenantKey}
          dcId={dcId ?? undefined}
          updateRequest={null}
          initialValues={{
            name: registerTarget.initialValue,
            code: registerTarget.initialValue
          }}
          onSuccess={(site) => handleMasterRegisterSuccess(site.code)}
          onClose={() => setRegisterTarget(null)}
        />
      )}
      {registerTarget?.kind === 'customer' && (
        <CustomerCreateModal
          open
          tenantKey={tenantKey}
          onSuccess={(customer) => handleMasterRegisterSuccess(customer.code)}
          onClose={() => setRegisterTarget(null)}
        />
      )}
      {registerTarget?.kind === 'shipmentCategory' && (
        <ShipmentCategoryCreateModal
          open
          tenantKey={tenantKey}
          onSuccess={(shipmentCategory) =>
            handleMasterRegisterSuccess(shipmentCategory.code)
          }
          onClose={() => setRegisterTarget(null)}
        />
      )}
      {registerTarget?.kind === 'truckModel' && (
        <TruckModelCreateModal
          open
          tenantKey={tenantKey}
          dcId={dcId ?? undefined}
          onSuccess={(truckModel) =>
            handleMasterRegisterSuccess(truckModel.code)
          }
          onClose={() => setRegisterTarget(null)}
        />
      )}
      {registerTarget?.kind === 'packageCategory' && (
        <PackageCategoryCreateModal
          open
          tenantKey={tenantKey}
          onSuccess={(packageCategory) =>
            handleMasterRegisterSuccess(packageCategory.code)
          }
          onClose={() => setRegisterTarget(null)}
        />
      )}
    </div>
  )
}
